import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { FiShoppingCart } from 'react-icons/fi'
import { FaWhatsapp } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { useCart } from '../../context/CartContext'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'
import ImageGalleryModal from './ImageGalleryModal'
import PaginationControls from './PaginationControls'

const WHATSAPP_BUTTON_OVERRIDE_CSS = `
button.permanent-whatsapp-button,
button.permanent-whatsapp-button:hover,
button.permanent-whatsapp-button:active,
button.permanent-whatsapp-button:focus,
button.permanent-whatsapp-button:visited,
button.permanent-whatsapp-button:disabled {
  background: #10B981 !important;
  background-color: #10B981 !important;
  background-image: none !important;
  color: #FFFFFF !important;
  border: 1px solid transparent !important;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3) !important;
  outline: none !important;
  text-decoration: none !important;
}
button.permanent-whatsapp-button:hover {
  background: #059669 !important;
  background-color: #059669 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:active {
  background: #047857 !important;
  background-color: #047857 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:focus {
  background: #047857 !important;
  background-color: #047857 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:visited {
  background: #10B981 !important;
  background-color: #10B981 !important;
  background-image: none !important;
}
button.permanent-whatsapp-button:disabled {
  opacity: 0.65 !important;
  cursor: not-allowed !important;
}
button.permanent-whatsapp-button .whatsapp-icon {
  color: #FFFFFF !important;
}
`

const ITEMS_PER_PAGE = 10

export default function PublicProductsSection({ websiteSlug, website, section, onAddToCart, onViewProduct, sectionFormStyling, useDefaultStyling = true, sectionType = 'products' }) {
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const hoverButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.hoverButtonBackgroundColor || formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.hoverButtonBackgroundColor,
    color: formColors?.hoverButtonTextColor || formColors?.buttonTextColor || DEFAULT_FORM_COLORS.hoverButtonTextColor
  } : { backgroundColor: '#2563EB', color: '#FFFFFF' }
  const { addToCart } = useCart()
  const [showGalleryModal, setShowGalleryModal] = useState(false)
  const [galleryItems, setGalleryItems] = useState([])
  const [galleryStartIndex, setGalleryStartIndex] = useState(0)
  const [productFilter, setProductFilter] = useState('')
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [addingToCart, setAddingToCart] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hoveredButtons, setHoveredButtons] = useState(new Set())
  const [hoveredCards, setHoveredCards] = useState(new Set())

  const closeGalleryModal = useCallback(() => setShowGalleryModal(false), [])

  useEffect(() => {
    console.log('[PublicProductsSection] Component mounted with:', {
      websiteSlug,
      websiteName: website?.name,
      websitePublicUrl: website?.publicUrl,
      websiteCategory: website?.category,
      websiteStatus: website?.status
    })
    
    if (websiteSlug || website?.publicUrl) {
      fetchProducts()
    }
  }, [websiteSlug, website?.publicUrl])

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [productFilter])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Try to use publicUrl from website object first, then fallback to slug
      const storeSlug = website?.publicUrl || websiteSlug
      
      if (!storeSlug) {
        console.error('[PublicProductsSection] No slug or publicUrl available')
        setError('Store information missing - cannot load products')
        setProducts([])
        setLoading(false)
        return
      }
      
      const apiUrl = `/store-payments/store/${storeSlug}/products`
      console.log('[PublicProductsSection] Fetching products from:', {
        apiUrl,
        storeSlug,
        websiteId: website?._id,
        websiteName: website?.name,
        websiteCategory: website?.category,
        websiteStatus: website?.status,
        publicUrl: website?.publicUrl
      })
      
      const response = await axios.get(apiUrl)
      
      console.log('[PublicProductsSection] Products fetched successfully:', {
        status: response.status,
        count: response.data.products?.length,
        store: response.data.website?.name,
        productsWithImage: response.data.products?.filter(p => p.image?.url).length,
        firstProductName: response.data.products?.[0]?.name,
        firstProductImage: response.data.products?.[0]?.image?.url,
        allProducts: response.data.products?.map(p => ({
          _id: p._id,
          name: p.name,
          price: p.price,
          hasImage: !!p.image?.url,
          status: p.status
        }))
      })
      
      setProducts(response.data.products || [])
      
      if (!response.data.products || response.data.products.length === 0) {
        console.warn('[PublicProductsSection] No products found for this store')
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load products'
      const diagnostics = error.response?.data?.diagnostics
      
      console.error('[PublicProductsSection] Error fetching products:', {
        message: errorMsg,
        status: error.response?.status,
        url: `/store-payments/store/${website?.publicUrl || websiteSlug}/products`,
        storeSlug: website?.publicUrl || websiteSlug,
        responseData: error.response?.data,
        diagnostics: diagnostics,
        fullError: error
      })
      
      // Build detailed error message
      let detailedError = errorMsg
      if (diagnostics) {
        const issues = []
        if (!diagnostics.websiteFound) {
          issues.push('❌ Website not found with publicUrl: ' + (website?.publicUrl || websiteSlug))
        }
        if (!diagnostics.isPublished) {
          issues.push('❌ Website status is ' + (diagnostics.websiteStatus || 'unknown') + ' (must be "published")')
        }
        if (diagnostics.userDisabled) {
          issues.push('❌ Website is disabled by user')
        }
        if (diagnostics.globalDisabled) {
          issues.push('❌ Website is globally disabled by admin')
        }
        if (issues.length > 0) {
          detailedError = issues.join('\n')
        }
      }
      
      setError(detailedError)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = (product) => {
    setAddingToCart(product._id)
    
    try {
      // Add product to cart with website info
      const productWithWebsite = {
        ...product,
        websiteId: website?._id,
        currency: product.currency || website?.paymentSettings?.currency || 'NGN'
      }
      
      addToCart(productWithWebsite)
      
      toast.success(`${product.name} added to cart!`, {
        duration: 2000,
        icon: '🛒'
      })

      console.log('[PublicProductsSection] Product added to cart:', {
        productName: product.name,
        productId: product._id
      })

      // Navigate to shop page for cart & checkout
      // const shopSlug = website?.publicUrl || websiteSlug
      // if (shopSlug) {
      //   navigate(`/store/${shopSlug}/shop`)
      // }

      // Call parent callback if provided
      if (onAddToCart) {
        onAddToCart(productWithWebsite)
      }
    } catch (error) {
      console.error('[PublicProductsSection] Error adding to cart:', error)
      toast.error('Failed to add product to cart')
    } finally {
      setTimeout(() => setAddingToCart(null), 300)
    }
  }

  const handleWhatsAppContact = (product) => {
    if (!product.whatsappNumber) return

    // Clean the phone number (remove spaces, dashes, etc.)
    const cleanNumber = product.whatsappNumber.replace(/[\s\-\(\)]/g, '')
    
    // Ensure it starts with +
    const phoneNumber = cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`
    
    // Create the message with product details
    const defaultMessage = `Hi! I'm interested in your product: ${product.name}`
    const customMessage = product.whatsappMessage || defaultMessage
    
    // Build the full message with product details
    const message = `${customMessage}\n\nProduct: ${product.name}\nPrice: ${product.currency} ${product.price}\n${product.description ? `Description: ${product.description}` : ''}`
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message.trim())
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`
    
    // Get button styling info for logging
    const screenWidth = window.innerWidth
    const buttonStyle = {
      backgroundColor: '#10B981',
      color: '#FFFFFF',
      hasImportant: true
    }
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank')
    
    console.log('[PublicProductsSection] WhatsApp contact initiated:', {
      productName: product.name,
      phoneNumber,
      message: message.trim(),
      buttonStyling: buttonStyle
    })
    
    // Log WhatsApp interaction to backend with styling verification
    try {
      fetch('/websites-public/log/whatsapp-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          websiteId: website?._id,
          websiteSlug: websiteSlug || website?.publicUrl,
          sectionType: 'products',
          itemName: product.name,
          itemId: product._id,
          phoneNumber: phoneNumber,
          timestamp: new Date().toISOString(),
          buttonClass: 'permanent-whatsapp-button',
          buttonStyling: buttonStyle,
          buttonState: 'default',
          deviceType: screenWidth < 768 ? 'mobile' : screenWidth < 1024 ? 'tablet' : 'desktop',
          screenWidth: screenWidth
        })
      }).catch(err => console.warn('[PublicProductsSection] Failed to log WhatsApp contact:', err.message))
    } catch (err) {
      console.warn('[PublicProductsSection] Error logging WhatsApp contact:', err.message)
    }
  }

  const openProductGallery = (product, startIndex = 0) => {
    if (!product) return
    const imgs = []
    // Product may have image.url or a gallery array that includes both images and videos
    if (product.gallery && product.gallery.length > 0) {
      product.gallery.forEach(g => {
        // Include gallery items that have either image or video
        if ((g.image?.url || g.video?.url)) {
          imgs.push(g)
        }
      })
    }
    // Fallback: if no gallery, collect single image
    if (imgs.length === 0 && product.image && product.image.url) {
      imgs.push({ image: { url: product.image.url } })
    }
    if (imgs.length === 0) return
    setGalleryItems(imgs)
    setGalleryStartIndex(startIndex)
    setShowGalleryModal(true)
  }

  const handleViewProduct = (product) => {
    if (onViewProduct) {
      onViewProduct(product)
      return
    }
    openProductGallery(product, 0)
  }


  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    const errorLines = error.split('\n')
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
        <p className="text-red-600 text-lg font-medium mb-3">⚠️ Error loading products</p>
        {errorLines.length > 1 ? (
          <div className="text-red-500 text-sm mt-2 space-y-2 text-left inline-block max-w-md">
            {errorLines.map((line, idx) => (
              <p key={idx} className="whitespace-pre-wrap">{line}</p>
            ))}
          </div>
        ) : (
          <p className="text-red-500 text-sm mt-2">{error}</p>
        )}
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200 text-left text-xs text-blue-700 max-w-md mx-auto">
          <p className="font-semibold mb-2">📋 Steps to fix:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Go to Admin → Websites → Edit this website</li>
            <li>Set Status to "Published"</li>
            <li>Set Category to "Ecommerce"</li>
            <li>Ensure Public URL is set</li>
            <li>Ensure products are created and active</li>
            <li>Refresh this page</li>
          </ul>
        </div>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-lg" style={textStyle}>No products available</p>
      </div>
    )
  }

  return (
    <div style={formStyle}>
      <style>{WHATSAPP_BUTTON_OVERRIDE_CSS}</style>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search products by name or description..."
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          className="input-field w-full"
        />
      </div>
      
      {/* Calculate filtered and paginated products */}
      {(() => {
        const filteredProducts = products.filter(p => {
          const q = (productFilter || '').toString().trim().toLowerCase()
          if (!q) return true
          const name = (p.name || '').toString().toLowerCase()
          const desc = (p.description || '').toString().toLowerCase()
          return name.includes(q) || desc.includes(q)
        })
        
        const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)
        const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
        const endIdx = startIdx + ITEMS_PER_PAGE
        const paginatedProducts = filteredProducts.slice(startIdx, endIdx)
        
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProducts.map(product => (
        <div 
          key={product._id} 
          className="rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200"
          style={isFormStyling ? {
            backgroundColor: hoveredCards.has(product._id) ? (sectionFormStyling.hoverBackgroundColor || sectionFormStyling.backgroundColor) : sectionFormStyling.backgroundColor,
            color: hoveredCards.has(product._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
          } : undefined}
          onMouseEnter={() => setHoveredCards(prev => new Set([...prev, product._id]))}
          onMouseLeave={() => setHoveredCards(prev => { const newSet = new Set(prev); newSet.delete(product._id); return newSet; })}
        >
          {/* Product Image */}
          {product.image?.url && (
            <div 
              className="relative h-48 bg-gray-100 overflow-hidden cursor-pointer hover:scale-105 transition-transform"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                if (e.stopImmediatePropagation) e.stopImmediatePropagation(); 
                console.log('[PublicProductsSection] Product image clicked:', product.name);
                handleViewProduct(product)
              }}
            >
              <img 
                src={product.image.url} 
                alt={product.name} 
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Product Details */}
          <div className="p-4 space-y-3">
            {/* Product Name */}
            <h3 className="font-semibold text-lg line-clamp-2" style={isFormStyling ? {
              color: hoveredCards.has(product._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
            } : undefined}>
              {product.name}
            </h3>

            {/* Product Description */}
            {product.description && (
              <p className="text-sm line-clamp-2" style={isFormStyling ? {
                color: hoveredCards.has(product._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
              } : undefined}>
                {product.description}
              </p>
            )}

            {/* Product Category */}
            {product.category && (
              <p className="text-xs font-medium uppercase" style={isFormStyling ? {
                color: hoveredCards.has(product._id) ? (sectionFormStyling.hoverTextColor || sectionFormStyling.textColor) : sectionFormStyling.textColor
              } : undefined}>
                {product.category}
              </p>
            )}

            {/* Product Stock */}
            {product.stock !== undefined && (
              <p className={`text-xs font-medium ${
                product.stock === 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {product.stock === -1 ? '✓ In Stock' : product.stock > 0 ? `✓ ${product.stock} in stock` : '✗ Out of Stock'}
              </p>
            )}

            {/* Price and Add to Cart Button */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <div>
                <p className="text-2xl font-bold text-primary-600">
                  {product.currency} {product.price.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Add to Cart Button - only show if cart is enabled for this product */}
                {product.cartEnabled !== false && (
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={addingToCart === product._id}
                    style={hoveredButtons.has(`cart-${product._id}`) && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
                    onMouseEnter={() => setHoveredButtons(prev => new Set([...prev, `cart-${product._id}`]))}
                    onMouseLeave={() => setHoveredButtons(prev => { const newSet = new Set(prev); newSet.delete(`cart-${product._id}`); return newSet; })}
                    className={`p-2 rounded-lg transition-all transform duration-200 ${
                      addingToCart === product._id
                        ? 'text-white scale-95'
                        : isButtonStyling
                          ? ''
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                    title="Add to cart"
                  >
                    {addingToCart === product._id ? (
                      <span className="text-sm">✓</span>
                    ) : (
                      <FiShoppingCart className="w-5 h-5" />
                    )}
                  </button>
                )}

                {/* Cart Disabled Indicator */}
                {product.cartEnabled === false && (
                  <div className="px-2 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs font-medium">
                    Cart Disabled
                  </div>
                )}

                {/* WhatsApp Contact Button - Always Green (Permanent Styling with !important) */}
                {product.whatsappNumber && (
                  <button
                    type="button"
                    onClick={() => {
                      console.log('[PublicProductsSection] WhatsApp button clicked for product:', {
                        productName: product.name,
                        productId: product._id,
                        phoneNumber: product.whatsappNumber,
                        timestamp: new Date().toISOString(),
                        buttonColor: 'permanent green (#10B981)',
                        styling: 'class-based override with valid inline style values'
                      })
                      handleWhatsAppContact(product)
                    }}
                    className="transition-colors duration-200 hover:shadow-md permanent-whatsapp-button"
                    style={{
                      backgroundColor: hoveredButtons.has(`whatsapp-${product._id}`) ? '#059669' : '#10B981',
                      color: '#FFFFFF',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.5rem',
                      transition: 'background-color 200ms',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                    }}
                    onMouseEnter={() => {
                      setHoveredButtons(prev => new Set([...prev, `whatsapp-${product._id}`]))
                    }}
                    onMouseLeave={() => {
                      setHoveredButtons(prev => { const newSet = new Set(prev); newSet.delete(`whatsapp-${product._id}`); return newSet; })
                    }}
                    title="Contact via WhatsApp"
                    aria-label={`Contact ${product.name} via WhatsApp`}
                  >
                    <FaWhatsapp className="w-4 h-4 whatsapp-icon" style={{ color: '#FFFFFF' }} />
                    <span>WhatsApp</span>
                  </button>
                )}

                {/* View button - open item details modal or gallery */}
                {(product.image?.url || (product.gallery && product.gallery.length > 0)) && (
                  <button
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation(); handleViewProduct(product) }}
                    style={hoveredButtons.has(`view-${product._id}`) && hoverButtonStyle ? hoverButtonStyle : buttonStyle}
                    onMouseEnter={() => setHoveredButtons(prev => new Set([...prev, `view-${product._id}`]))}
                    onMouseLeave={() => setHoveredButtons(prev => { const newSet = new Set(prev); newSet.delete(`view-${product._id}`); return newSet; })}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors duration-200 ${isButtonStyling ? '' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                    title="View"
                  >
                    View
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
            </div>
            
            {/* Show pagination if more than one page */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                sectionFormStyling={sectionFormStyling}
                section={section}
                sectionType="pagination-controls"
                loading={loading}
              />
            )}
          </>
        )
      })()}

      <ImageGalleryModal
        isOpen={showGalleryModal}
        images={galleryItems}
        startIndex={galleryStartIndex}
        onClose={closeGalleryModal}
        sectionFormStyling={sectionFormStyling}
        section={section}
        sectionType="image-gallery-modal"
      />
    </div>
  )
}

PublicProductsSection.propTypes = {
  websiteSlug: PropTypes.string,
  website: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
    status: PropTypes.string,
    publicUrl: PropTypes.string,
    paymentSettings: PropTypes.shape({
      currency: PropTypes.string
    })
  }),
  section: PropTypes.shape({
    _id: PropTypes.string
  }),
  onAddToCart: PropTypes.func,
  onViewProduct: PropTypes.func,
  sectionFormStyling: PropTypes.object,
  sectionType: PropTypes.string
}